import produce, { applyPatches } from "immer";
import { useReducer, useRef } from "react";
import {
  CANVAS_OUTLINE_BEATS,
  CANVAS_OUTLINE_HEROS_JOURNEY_ID,
  CANVAS_OUTLINE_ROMANCING_THE_BEAT_ID,
  CANVAS_OUTLINE_SAVE_THE_CAT_ID,
  CANVAS_OUTLINE_TYPES,
  OUTLINE_CARD_TYPE,
  OUTLINE_HEADER_CARD_TYPE,
  TEXT_CARD_TYPE,
} from "../../../../../src/shared/canvasConstants";
import { enablePatches } from "immer";
import { findVal, getCircleRadius, getPointsOnEllipse, makeId } from "../../../Utils";
import { DEFAULT_CARD_SIZE } from "../Card";
import { romanceFormation, saveTheCatFormation } from "../constants";
enablePatches();

export const LOAD_ACTION = "LOAD";

// Card actions
export const SELECT_ALL_ACTION = "SELECT_ALL";
export const SELECT_ACTION = "SELECT";
export const DESELECT_ACTION = "DESELECT";
export const DESELECT_ALL_ACTION = "DESELECT_ALL";
export const ADD_CARD_ACTION = "ADD";
export const ADD_OUTLINE_GROUP_ACTION = "ADD_OUTLINE_GROUP";
export const DELETE_ACTION = "DELETE";
export const SET_SIZE_ACTION = "SET_SIZE";
export const SET_POSITION_ACTION = "SET_POSITION";
export const SET_CONTENT_ACTION = "SET_CONTENT";
export const DISTRIBUTE_EVENLY_ACTION = "DISTRIBUTE_EVENLY";
export const SET_DATA_ACTION = "SET_DATA";
export const SET_DATA_SILENT_ACTION = "SET_DATA_SILENT";

// Group actions
export const ADD_GROUP_ACTION = "ADD_GROUP";
export const SET_GROUP_ACTION = "SET_GROUP";
export const CONVERT_GROUP_ACTION = "CONVERT_GROUP";

export const UNDO = "UNDO";
export const REDO = "REDO";

// const changes = {};
// let currentVersion = -1;
const noOfVersionsSupported = 4000;
const undoableActions = [
  CONVERT_GROUP_ACTION, //
  DELETE_ACTION,
  ADD_CARD_ACTION,
  SET_CONTENT_ACTION,
  SET_POSITION_ACTION,
  SET_DATA_ACTION,
  SET_SIZE_ACTION,
  ADD_OUTLINE_GROUP_ACTION,
];

/**
 * memoized array of ids of currently selected cards
 */
const selectedCards = (draft) => {
  if (!draft) {
    return [];
  }

  const selected = [];
  Object.entries(draft).forEach(([key, value]) => {
    if (value.selected) {
      selected.push(key);
    }
  });

  return selected;
};

const getHighestIndexDraft = (draft) => {
  let highest = 0;

  for (const id of Object.keys(draft.cards)) {
    const card = draft.cards[id];
    if (card.index > highest) {
      highest = card.index;
    }
  }

  // console.log("highest", highest);

  return highest;
};

const changes = {};
let currentVersion = -1;

const constructCardData = (
  draft,
  {
    id = null,
    x,
    y,
    width = DEFAULT_CARD_SIZE[0],
    height = DEFAULT_CARD_SIZE[1],
    content = "",
    selected = true,
    type = TEXT_CARD_TYPE,
    generating = false,
    generated = false,
    sourceCardIds = null,
    initialEditing = true,
    title = null,
    placeholder = null,
    autoHeight = false,
    data = {}, // custom data that can be associated with a particular card type
  }
) => {
  let theId = id;
  if (!theId) {
    theId = makeId(6); // make id if not passed in
  }

  return {
    id: theId,
    title: title,
    content: content,
    selected: selected,
    generating: generating,
    placeholder: placeholder,
    width: width,
    height: height,
    type: type,
    x: x,
    y: y,
    autoHeight: autoHeight,
    index: getHighestIndexDraft(draft) + 1,
    sourceCardIds: sourceCardIds,
    generated: generated,
    createdAt: new Date().getTime(),
    initialEditing: initialEditing,
    data: data,
  };
};

const createOutlineGroupBody = (
  draft,
  cards, //
  groups,
  { type = CANVAS_OUTLINE_HEROS_JOURNEY_ID, headerCardId, groupId, selected = false }
) => {
  const cardIds = [];
  const headerCard = cards[headerCardId];

  const template = CANVAS_OUTLINE_BEATS[type];
  const N = template.length;
  const BUFFER = 100;

  let points;
  if (type === CANVAS_OUTLINE_SAVE_THE_CAT_ID) {
    points = saveTheCatFormation([headerCard.x + DEFAULT_CARD_SIZE[0] / 2, headerCard.y + DEFAULT_CARD_SIZE[1] + 200]);
  } else if (type === CANVAS_OUTLINE_ROMANCING_THE_BEAT_ID) {
    points = romanceFormation([headerCard.x + DEFAULT_CARD_SIZE[0] / 2, headerCard.y + DEFAULT_CARD_SIZE[1] + 200]);
  } else {
    // calculate the center such that the header card is in the right place
    const radius = getCircleRadius(DEFAULT_CARD_SIZE[0], DEFAULT_CARD_SIZE[1], N) + BUFFER;
    const center = [headerCard.x + DEFAULT_CARD_SIZE[0] / 2 - 25, headerCard.y + radius + DEFAULT_CARD_SIZE[1] + 150];
    points = getPointsOnEllipse(radius * 1.25, radius, center[0], center[1], N);
  }

  // add the rest of the outline cards
  points.forEach((point, i) => {
    const { title, description, id } = template[i];

    const data = constructCardData(draft, {
      x: point[0],
      y: point[1],
      width: DEFAULT_CARD_SIZE[0] + 50,
      selected: selected,
      title: title,
      placeholder: description,
      autoHeight: true,
      data: {
        beatId: id,
      },
      type: OUTLINE_CARD_TYPE,
      initialEditing: false,
    });

    cards[data.id] = data;

    cardIds.push(data.id);
  });

  groups[groupId].cards = groups[groupId].cards.concat(cardIds);
};

const useCanvasReducer = () => {
  // const changes = useRef({});
  // const currentVersion = useRef(-1);
  const nonces = useRef({});

  const [state, dispatch] = useReducer(
    (state, action) => {
      return produce(
        // Default value

        state,
        (draft) => {
          let cards = draft.cards;
          let groups = draft.groups;

          if (![SET_POSITION_ACTION, DESELECT_ACTION, DESELECT_ALL_ACTION, SELECT_ALL_ACTION].includes(action.type)) {
            console.log("[REDUCER]", action);
            // console.log(draft);
          }

          // if (!skip) {
          switch (action.type) {
            case ADD_CARD_ACTION:
              const { data } = action;

              const cardData = constructCardData(draft, data);
              cards[cardData.id] = cardData;
              break;

            case ADD_GROUP_ACTION:
              groups[action.id] = action.data;
              break;

            case ADD_OUTLINE_GROUP_ACTION: {
              const { title, center } = action;
              const WIDTH = 600;

              // outline header card positioned at top
              const topPoint = center;
              const type = CANVAS_OUTLINE_HEROS_JOURNEY_ID;

              // create the header card
              const outlineHeaderCardData = constructCardData(draft, {
                type: OUTLINE_HEADER_CARD_TYPE,
                data: {
                  type: type,
                },
                width: WIDTH,
                x: topPoint[0] - WIDTH / 4,
                y: topPoint[1] - DEFAULT_CARD_SIZE[1] - 160,
                autoHeight: true,
                initialEditing: false,
              });

              cards[outlineHeaderCardData.id] = outlineHeaderCardData;

              const groupId = makeId(6);

              // create the group
              groups[groupId] = {
                id: groupId,
                type: OUTLINE_CARD_TYPE,
                title: title,
                cards: [outlineHeaderCardData.id],
              };

              createOutlineGroupBody(draft, cards, groups, {
                title: title,
                type: type,
                headerCardId: outlineHeaderCardData.id,
                groupId,
                selected: true,
              });

              break;
            }

            case CONVERT_GROUP_ACTION: {
              const { id, outlineType } = action;

              if (!outlineType || !CANVAS_OUTLINE_TYPES.find((i) => i.value === outlineType)) {
                throw new Error("Invalid outline type:", outlineType);
              }

              const headerCard = cards[id];
              const group = findVal(groups, (g) => g.cards[0] === id);

              group.cards.slice(1).forEach((cardId) => {
                delete cards[cardId];
              });

              group.cards = group.cards.slice(0, 1); // delete the rest of the cards

              createOutlineGroupBody(draft, cards, groups, {
                headerCardId: id,
                groupId: group.id,
                type: outlineType,
              });

              headerCard.data.type = outlineType;
              break;
            }

            case DELETE_ACTION:
              if (action.ids.length > 0) {
                let toDelete = []; // build up list of ids to delete

                let groupDelete = false;

                action.ids.forEach((id) => {
                  // For outline header cards, we delete all of the group's cards
                  if (cards[id].type === OUTLINE_HEADER_CARD_TYPE) {
                    // get the group that contains the header card
                    const groupId = Object.keys(groups).find((groupId) => {
                      return groups[groupId].cards.find((cardId) => cardId === id) !== undefined;
                    });

                    // select all of them
                    if (groupId) {
                      console.log("cards", groups[groupId].cards);
                      groups[groupId].cards.forEach((cardId) => {
                        toDelete.push(cardId);
                      });
                    } else {
                      console.log("coudn't find group for header card");
                    }

                    groupDelete = true;
                  } else {
                    toDelete.push(id);
                  }
                });

                toDelete = [...new Set(toDelete)]; // get unique ids

                // First remove the card from any of the groups it is contained in
                // const groupsWithRemovedCard = produce(groups, (draft) => {
                toDelete.forEach((cardId) => {
                  // first delete from groups
                  Object.keys(groups).forEach((groupId) => {
                    const group = groups[groupId];

                    if (group.cards.includes(cardId)) {
                      // console.log(`found card ${cardId} in group ${groupId}. deleting`);
                      groups[groupId].cards = groups[groupId].cards.filter((id) => id !== cardId);
                    }
                  });
                });
                // });

                // Prune empty groups

                Object.keys(groups).forEach((groupId) => {
                  const group = groups[groupId];

                  if (group.cards.length === 0) {
                    delete groups[groupId];
                  }
                });

                toDelete.forEach((id) => {
                  delete cards[id];
                });
              }
              break;

            case DESELECT_ACTION:
              if (cards[action.id]) {
                cards[action.id].selected = false;
              } else {
                throw new Error("Attempted to deselect a card that doesn't exist. id: " + action.id);
              }
              break;

            case DESELECT_ALL_ACTION:
              selectedCards(cards).forEach((id) => {
                cards[id].selected = false;
              });
              break;

            case DISTRIBUTE_EVENLY_ACTION:
              let cardArray = action.ids.map((id) => cards[id]);
              // console.log(cardArray);
              cardArray.sort((a, b) => {
                if (a.y < b.y) {
                  return -1;
                } else {
                  return 1;
                }
              });

              if (cardArray.length === 0) {
                break;
              }

              let currTop = cardArray[0].y;
              let newTops = [];
              for (const card of cardArray) {
                // console.log("currTop", currTop);
                newTops.push(currTop);
                // console.log("height", card.height);
                currTop += card.height + 40;
              }

              let i = 0;
              for (const top of newTops) {
                cards[cardArray[i].id].y = top;
                delete cards[cardArray[i].id].distribute;
                i++;
              }

              break;

            case LOAD_ACTION:
              // draft = action.cards;
              draft.cards = action.cards; // replace the state entirely
              draft.groups = action.groups;
              break;

            case SELECT_ACTION: {
              const { id } = action;
              cards[id].selected = true;
              cards[id].index = getHighestIndexDraft(draft) + 1; // bump to top of z-index
              break;
            }

            case SELECT_ALL_ACTION:
              Object.keys(cards).forEach((id) => {
                cards[id].selected = true;
                cards[id].index = getHighestIndexDraft(draft) + 1; // bump to top of z-index
              });
              break;

            case SET_CONTENT_ACTION:
              cards[action.id].content = action.content;

              break;

            case SET_GROUP_ACTION:
              groups[action.id] = Object.assign(groups[action.id], action.data);
              break;

            case SET_DATA_SILENT_ACTION:
            case SET_DATA_ACTION:
              cards[action.id] = Object.assign(cards[action.id], action.data);
              break;

            case SET_POSITION_ACTION:
              let idsToMove;

              if (!cards[action.id].selected) {
                // if the current card isn't selected, then we move just that one
                idsToMove = [action.id];
              } else {
                idsToMove = selectedCards(cards);
              }

              idsToMove.forEach((id) => {
                const c = cards[id];
                c.x = c.x + action.deltaX;
                c.y = c.y + action.deltaY;
              });

              break;

            case SET_SIZE_ACTION:
              const { id, width, height, autoHeight } = action;


              cards[id].width = width;
              cards[id].height = height;
              if (autoHeight !== undefined) {
                cards[id].autoHeight = autoHeight;
              }
              // console.log(cards[id]);
              // cards[action.id].autoHeight = false; // once resized, no longer auto height
              break;

            case UNDO:
              console.log("version", currentVersion);
              console.log("changes", changes);

              if (nonces.current[action.nonce]) {
                // prevent undoing the same action twice via nonce
                console.log("using nonce value");
                return nonces.current[action.nonce];
              }

              if (!changes[currentVersion]) {
                console.log("nothing left to undo!");
                break;
              }

              const change = changes[currentVersion].undo;
              currentVersion--;

              console.log("change", change);

              console.log("state", state);

              const out = applyPatches(state, change);
              console.log("applied", out);

              if (action.nonce) {
                nonces.current[action.nonce] = out;
              }

              return out;

            case REDO:
              console.log("nonce value", nonces.current[action.nonce]);
              if (action.nonce && nonces.current[action.nonce]) {
                console.log("using nonce value");
                return nonces.current[action.nonce];
              }

              if (!changes[currentVersion + 1]) {
                console.log("nothing left to redo!");
                break;
              }

              currentVersion++;

              const redoOut = applyPatches(state, changes[currentVersion].redo);

              // console.log("redoOut", redoOut);

              if (action.nonce) {
                nonces.current[action.nonce] = redoOut;
              }

              return redoOut;

            default:
              break;
          }
          // }
        },

        (patches, inversePatches) => {
          if (
            undoableActions.indexOf(action.type) !== -1 && // only if an action is undoable
            patches.length > 0 && // and there are patches to apploy
            inversePatches.length > 0 &&
            !nonces.current[action.nonce] // and the patch hasn't already been applied
          ) {
            // console.log("patches", patches);
            // [ { op: 'replace', path: [ 'age' ], value: 40 } ]

            // console.log("inversePatches", inversePatches);
            // [ { op: 'replace', path: [ 'age' ], value: 32 } ]

            // console.log("changes", changes);

            // If the previous two patches are only card moves, then we merge them
            if (changes[currentVersion] && changes[currentVersion - 1]) {
              const previous2CardId = patchIsMove(changes[currentVersion - 1].redo);
              const previous1CardId = patchIsMove(changes[currentVersion].redo);
              const currentCardId = patchIsMove(patches);

              if (currentCardId && previous1CardId && previous2CardId && currentCardId === previous1CardId && previous1CardId === previous2CardId) {
                // replace the change
                changes[currentVersion] = {
                  redo: combineMovePatches(changes[currentVersion].redo, patches),
                  undo: combineMovePatches(inversePatches, changes[currentVersion].undo),
                };
                // console.log("only move");
                nonces.current[action.nonce] = true; // mark as saved

                return;
              }
            }

            // apply the patch normally

            currentVersion++;

            changes[currentVersion] = {
              redo: patches,
              undo: inversePatches,
            };

            delete changes[currentVersion + 1];
            delete changes[currentVersion - noOfVersionsSupported];

            nonces.current[action.nonce] = true; // mark as saved
          }
        }
      );
    },
    {
      cards: null,
      groups: null,
    }
  );

  return [
    state,
    (obj) => {
      dispatch(Object.assign(obj, { nonce: makeId(6) }));
    },
  ];
};

/**
 * Returns true if the patches are only card moves
 * @param {Array} patches
 * @returns
 */
const patchIsMove = (patches) => {
  let onlyMove = true;
  let cardId = null;
  patches.forEach((p) => {
    if (p.op === "replace" && p.path[0] === "cards" && (p.path[p.path.length - 1] === "x" || p.path[p.path.length - 1] === "y")) {
      cardId = p.path[1];
    } else {
      onlyMove = false;
    }
  });

  if (onlyMove && cardId) {
    return cardId;
  } else {
    return false;
  }
};

/**
 * Given two patches that are ONLY card moves, this will combine them
 * @param {Array} prev
 * @param {Array} curr
 * @returns
 */
const combineMovePatches = (prev, curr) => {
  const cardPatches = {};
  const out = [];

  prev.forEach((patch) => {
    if (!cardPatches[patch.path[1]]) cardPatches[patch.path[1]] = {};
    cardPatches[patch.path[1]][patch.path[2]] = patch.value;
  });

  curr.forEach((patch) => {
    if (!cardPatches[patch.path[1]]) cardPatches[patch.path[1]] = {};
    cardPatches[patch.path[1]][patch.path[2]] = patch.value;
  });

  Object.keys(cardPatches).forEach((cardId) => {
    Object.keys(cardPatches[cardId]).forEach((dim) => {
      out.push({
        op: "replace",
        path: ["cards", cardId, dim],
        value: cardPatches[cardId][dim],
      });
    });
  });

  return out;
};

export default useCanvasReducer;
